import {
  setBusinessAdvisorMapsData,
  setBusinessAdvisorMapsToBrandsData,
  setDestinationTablesData,
  setGapRulesData,
  setInterfaceData,
} from "../../../features/advisorSlice";
import { setDisplayErrorSnackBar } from "../../../features/userSlice";
import {
  dismissPersistentSnackbar,
  showPersistentSnackbar,
} from "../../../components/PersistentErrorSnackbar/persistentErrorSnackbarStore";
import { AdvisorMapsService } from "../../../services/api/advisor-maps.endpoints";
import store from "../../../store";

async function getInterface(oauthTokenHeader: string) {
  try {
    const response = await AdvisorMapsService.getInterface(oauthTokenHeader);
    if (!response.status || response.status !== 200) {
      throw new Error();
    } else {
      store.dispatch(setInterfaceData(response?.data?.data));
    }
  } catch (error: any) {
    store.dispatch(
      setDisplayErrorSnackBar({
        message: error?.parsedText || "Unknown error occurred",
        type: "error",
      }),
    );
  }
}

async function getDestinationTables(oauthTokenHeader: any) {
  try {
    const response =
      await AdvisorMapsService.getDestinationTables(oauthTokenHeader);
    if (!response.status || response.status !== 200) {
      throw new Error();
    } else {
      store.dispatch(setDestinationTablesData(response?.data?.data));
    }
  } catch (error: any) {
    store.dispatch(
      setDisplayErrorSnackBar({
        message: error?.parsedText || "Unknown error occurred",
        type: "error",
      }),
    );
  }
}

/**
 * The destination table is an optional filter - only the interface is required.
 * Returns whether the call succeeded so callers can gate the table on it.
 */
async function getBusinessAdvisorMaps(
  oauthTokenHeader: string,
  interfaceID: string | number | null,
  destinationTableID?: string | number | null,
): Promise<boolean> {
  if (!interfaceID) {
    store.dispatch(
      setDisplayErrorSnackBar({
        message: "Please select an interface.",
        type: "error",
      }),
    );
    return false;
  }
  try {
    const response = await AdvisorMapsService.getBusinessAdvisorMaps(
      oauthTokenHeader,
      interfaceID,
      destinationTableID,
    );
    if (
      !response?.status ||
      (response?.status !== 200 && response?.status !== 204)
    ) {
      throw new Error();
    } else if (response?.status === 204) {
      store.dispatch(
        setDisplayErrorSnackBar({
          message: "No content available",
          type: "warning",
        }),
      );
      store.dispatch(setBusinessAdvisorMapsData([]));
    } else {
      store.dispatch(setBusinessAdvisorMapsData(response?.data?.data));
    }
    return true;
  } catch (error: any) {
    store.dispatch(
      setDisplayErrorSnackBar({
        message: error?.parsedText || "Unknown error occurred",
        type: "error",
      }),
    );
    return false;
  }
}

async function getBusinessAdvisorMapsToBrands(
  oauthTokenHeader: string,
  mapID: string | number | null,
): Promise<boolean> {
  if (!mapID) return false;
  try {
    const response = await AdvisorMapsService.getBusinessAdvisorMapsToBrands(
      oauthTokenHeader,
      mapID,
    );
    if (
      !response?.status ||
      (response?.status !== 200 && response?.status !== 204)
    ) {
      throw new Error();
    } else if (response?.status === 204) {
      store.dispatch(setBusinessAdvisorMapsToBrandsData(null));
    } else {
      store.dispatch(setBusinessAdvisorMapsToBrandsData(response?.data?.data));
    }
    return true;
  } catch (error: any) {
    store.dispatch(
      setDisplayErrorSnackBar({
        message: error?.parsedText || "Unknown error occurred",
        type: "error",
      }),
    );
    return false;
  }
}

/**
 * Every gap rule. Callers narrow them per metric row by destination table and
 * target field, so the whole set is fetched once.
 */
async function getGapRules(oauthTokenHeader: string) {
  try {
    const response = await AdvisorMapsService.getGapRules(oauthTokenHeader);
    if (!response.status || response.status !== 200) {
      throw new Error();
    } else {
      store.dispatch(setGapRulesData(response?.data?.data));
    }
  } catch (error: any) {
    store.dispatch(
      setDisplayErrorSnackBar({
        message: error?.parsedText || "Unknown error occurred",
        type: "error",
      }),
    );
  }
}

/**
 * Creates an Advisor map. Returns whether it was created so the caller can
 * navigate away only on success.
 */
async function createAdvisorMap(
  oauthTokenHeader: string,
  payload: any,
): Promise<boolean> {
  try {
    const response = await AdvisorMapsService.createAdvisorMap(
      oauthTokenHeader,
      payload,
    );
    if (
      !response?.status ||
      (response?.status !== 200 && response?.status !== 201)
    ) {
      throw new Error();
    }
    dismissPersistentSnackbar();
    store.dispatch(
      setDisplayErrorSnackBar({
        message: "Map created successfully",
        type: "success",
      }),
    );
    return true;
  } catch (error: any) {
    // Backend errors stay on screen until the user dismisses them.
    showPersistentSnackbar(
      error?.parsedText ||
        "Failed to create map. Please check the details and try again.",
    );
    return false;
  }
}

/**
 * Saves the editable part of a map - its name and gap rule assignments.
 * Returns whether it saved so the caller can navigate away only on success.
 */
async function updateBusinessAdvisorMap(
  oauthTokenHeader: string,
  mapID: string | number,
  payload: any,
): Promise<boolean> {
  try {
    const response = await AdvisorMapsService.updateBusinessAdvisorMap(
      oauthTokenHeader,
      mapID,
      payload,
    );
    if (
      !response?.status ||
      (response?.status !== 200 &&
        response?.status !== 201 &&
        response?.status !== 204)
    ) {
      throw new Error();
    }
    dismissPersistentSnackbar();
    store.dispatch(
      setDisplayErrorSnackBar({
        message: "Map updated successfully",
        type: "success",
      }),
    );
    return true;
  } catch (error: any) {
    // Backend errors stay on screen until the user dismisses them.
    showPersistentSnackbar(
      error?.parsedText ||
        "Failed to update map. Please check the details and try again.",
    );
    return false;
  }
}

/**
 * Deletes a map. Returns whether it was deleted so the caller can refresh the
 * list only when something actually changed.
 */
async function deleteAdvisorMap(
  oauthTokenHeader: string,
  mapID: string | number,
): Promise<boolean> {
  try {
    const response = await AdvisorMapsService.deleteAdvisorMap(
      oauthTokenHeader,
      mapID,
    );
    if (
      !response?.status ||
      (response?.status !== 200 &&
        response?.status !== 202 &&
        response?.status !== 204)
    ) {
      throw new Error();
    }
    dismissPersistentSnackbar();
    store.dispatch(
      setDisplayErrorSnackBar({
        message: "Map deleted successfully",
        type: "success",
      }),
    );
    return true;
  } catch (error: any) {
    // Backend errors stay on screen until the user dismisses them.
    showPersistentSnackbar(
      error?.parsedText || "Failed to delete map. Please try again later.",
    );
    return false;
  }
}

/**
 * Creates a gap rule. Returns whether it was created so the caller can close
 * the dialog and refresh the list only on success.
 */
async function createGapRule(
  oauthTokenHeader: string,
  payload: any,
): Promise<boolean> {
  try {
    const response = await AdvisorMapsService.createGapRule(
      oauthTokenHeader,
      payload,
    );
    if (
      !response?.status ||
      (response?.status !== 200 && response?.status !== 201)
    ) {
      throw new Error();
    }
    dismissPersistentSnackbar();
    store.dispatch(
      setDisplayErrorSnackBar({
        message: "Gap rule created successfully",
        type: "success",
      }),
    );
    return true;
  } catch (error: any) {
    // Backend errors stay on screen until the user dismisses them.
    showPersistentSnackbar(
      error?.parsedText ||
        "Failed to create gap rule. Please check the details and try again.",
    );
    return false;
  }
}

/**
 * Updates a gap rule. Returns whether it saved so the caller can close the
 * dialog and refresh the list only on success.
 */
async function updateGapRule(
  oauthTokenHeader: string,
  gapRuleID: string | number,
  payload: any,
): Promise<boolean> {
  try {
    const response = await AdvisorMapsService.updateGapRule(
      oauthTokenHeader,
      gapRuleID,
      payload,
    );
    if (
      !response?.status ||
      (response?.status !== 200 &&
        response?.status !== 201 &&
        response?.status !== 204)
    ) {
      throw new Error();
    }
    dismissPersistentSnackbar();
    store.dispatch(
      setDisplayErrorSnackBar({
        message: "Gap rule updated successfully",
        type: "success",
      }),
    );
    return true;
  } catch (error: any) {
    // Backend errors stay on screen until the user dismisses them.
    showPersistentSnackbar(
      error?.parsedText ||
        "Failed to update gap rule. Please check the details and try again.",
    );
    return false;
  }
}

export const AdvisorMapsEndpointLogic = {
  getInterface,
  getDestinationTables,
  getBusinessAdvisorMaps,
  getBusinessAdvisorMapsToBrands,
  getGapRules,
  createGapRule,
  updateGapRule,
  createAdvisorMap,
  updateBusinessAdvisorMap,
  deleteAdvisorMap,
};
